import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { DATASETS } from '../../lib/datasets'
import { loadDataset } from '../../lib/data'
import { recordSearchEvent, selectSearchResult } from '../../lib/intelligence'
import type { DatasetKey } from '../../types'
import { OPERATIONAL_TOOLS, type ToolKey } from './OperationalTools'

export type GlobalSearchDestination =
  | 'inicio'
  | ToolKey
  | DatasetKey
  | 'vencimento-grupo'
  | 'devolucao-cc'
  | 'titularidade-religa'
  | 'implantacao-agua-esgoto'

type Props = {
  onNavigate: (destination: GlobalSearchDestination, prefill?: string) => void
}

type SearchResult = {
  id: string
  destination: GlobalSearchDestination
  title: string
  subtitle: string
  group: string
  score: number
  prefill?: string
}

type DatasetStore = Partial<Record<DatasetKey, Record<string, unknown>[]>>

const EXTRA_MODULES: Array<{ destination: GlobalSearchDestination; title: string; subtitle: string; keywords: string }> = [
  { destination: 'inicio', title: 'Início', subtitle: 'Visão geral das funções disponíveis', keywords: 'inicio home visão geral atalhos operação' },
  { destination: 'vencimento-grupo', title: 'Vencimento por Grupo', subtitle: 'Consulta os vencimentos permitidos pelo Grupo de Leitura em calendário', keywords: 'vencimento grupo leitura data datas calendario titular matricula' },
  { destination: 'titularidade-religa', title: 'Titularidade / Religação a Pedido', subtitle: 'Flow documental por vínculo e pendências', keywords: 'troca titularidade religacao pedido inquilino proprietario inventario procuracao' },
  { destination: 'implantacao-agua-esgoto', title: 'Implantação Água / Esgoto', subtitle: 'Flow de rede, extensão, pavimento, ramal e TIL', keywords: 'implantacao agua esgoto ligacao nova rede arcgis til ramal extensao' },
]

function normalize(value: unknown) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function recordText(record: Record<string, unknown>) {
  return Object.values(record)
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .filter((value) => typeof value === 'string' || typeof value === 'number')
    .join(' ')
}

function fieldText(record: Record<string, unknown>, fields: string[]) {
  return fields
    .map((field) => {
      const value = record[field]
      if (Array.isArray(value)) return value.join(' · ')
      return value == null ? '' : String(value)
    })
    .filter(Boolean)
    .join(' · ')
}

function scoreText(searchable: string, query: string, title = '') {
  const haystack = normalize(searchable)
  const needle = normalize(query)
  const normalizedTitle = normalize(title)
  if (!needle) return 0

  let score = 0
  if (normalizedTitle === needle) score += 1000
  else if (normalizedTitle.startsWith(needle)) score += 700
  else if (normalizedTitle.includes(needle)) score += 500

  if (haystack === needle) score += 600
  else if (haystack.includes(needle)) score += 320

  const words = needle.split(/\s+/).filter((word) => word.length > 1)
  let matched = 0
  words.forEach((word) => {
    if (haystack.includes(word)) {
      matched += 1
      score += normalizedTitle.includes(word) ? 85 : 38
    }
  })

  if (words.length > 1 && matched === words.length) score += 180
  if (words.length > 1 && matched === 0) return 0
  return score
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName.toLocaleLowerCase('pt-BR')
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable
}

export function GlobalSearch({ onNavigate }: Props) {
  const { profile, offlineProfile } = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const searchIdRef = useRef('')
  const trackedQueryRef = useRef('')
  const [query, setQuery] = useState('')
  const [store, setStore] = useState<DatasetStore>({})
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(false)

  const ensureLoaded = useCallback(async () => {
    if (loaded || loading) return
    setLoading(true)
    try {
      const entries = await Promise.all(DATASETS.map(async (dataset) => {
        try {
          const result = await loadDataset(dataset.key)
          return [dataset.key, result.records] as const
        } catch {
          return [dataset.key, []] as const
        }
      }))
      setStore(Object.fromEntries(entries) as DatasetStore)
      setLoaded(true)
    } finally {
      setLoading(false)
    }
  }, [loaded, loading])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const ctrlK = (event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase('pt-BR') === 'k'
      const slash = event.key === '/' && !isEditableTarget(event.target)
      if (!ctrlK && !slash) return
      event.preventDefault()
      void ensureLoaded()
      setOpen(true)
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [ensureLoaded])

  const results = useMemo(() => {
    const trimmed = query.trim()
    if (!trimmed) return []

    const found: SearchResult[] = []

    OPERATIONAL_TOOLS.forEach((tool) => {
      const score = scoreText(`${tool.label} ${tool.description}`, trimmed, tool.label)
      if (score > 0) found.push({
        id: `tool:${tool.key}`,
        destination: tool.key,
        title: tool.label,
        subtitle: tool.description,
        group: 'Ferramenta',
        score,
      })
    })

    EXTRA_MODULES.forEach((module) => {
      const score = scoreText(`${module.title} ${module.subtitle} ${module.keywords}`, trimmed, module.title)
      if (score > 0) found.push({
        id: `module:${module.destination}`,
        destination: module.destination,
        title: module.title,
        subtitle: module.subtitle,
        group: module.destination.includes('religa') || module.destination.includes('implantacao') ? 'Flow guiado' : 'Ferramenta',
        score,
      })
    })

    DATASETS.forEach((dataset) => {
      const moduleScore = scoreText(`${dataset.label} ${dataset.description}`, trimmed, dataset.label)
      if (moduleScore > 0) found.push({
        id: `dataset:${dataset.key}`,
        destination: dataset.key,
        title: dataset.label,
        subtitle: dataset.description,
        group: 'Consulta oficial',
        score: moduleScore + 80,
        prefill: trimmed,
      })

      const records = store[dataset.key] ?? []
      records.forEach((record, index) => {
        const title = fieldText(record, dataset.titleFields) || dataset.label
        const detail = fieldText(record, dataset.detailFields.slice(0, 3))
        const searchable = `${recordText(record)} ${dataset.label}`
        const score = scoreText(searchable, trimmed, title)
        if (score <= 0) return
        found.push({
          id: `record:${dataset.key}:${String(record.id ?? index)}`,
          destination: dataset.key,
          title,
          subtitle: detail || dataset.description,
          group: dataset.label,
          score: score + 120,
          prefill: trimmed,
        })
      })
    })

    return found
      .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title, 'pt-BR'))
      .slice(0, 12)
  }, [query, store])

  useEffect(() => {
    const trimmed = query.trim()
    if (!profile?.ativo || offlineProfile || !navigator.onLine || !loaded || loading || trimmed.length < 2) return

    const timer = window.setTimeout(() => {
      const normalized = normalize(trimmed)
      if (!normalized || trackedQueryRef.current === normalized) return
      const searchId = crypto.randomUUID()
      trackedQueryRef.current = normalized
      searchIdRef.current = searchId
      recordSearchEvent(profile.id, searchId, trimmed, results.length).catch(() => undefined)
    }, 700)

    return () => window.clearTimeout(timer)
  }, [query, results.length, loaded, loading, profile?.id, profile?.ativo, offlineProfile])

  function select(result: SearchResult) {
    if (searchIdRef.current) {
      selectSearchResult(searchIdRef.current, result.id, result.title).catch(() => undefined)
    }
    onNavigate(result.destination, result.prefill)
    setOpen(false)
  }

  function updateQuery(value: string) {
    setQuery(value)
    setOpen(true)
    trackedQueryRef.current = ''
    searchIdRef.current = ''
    void ensureLoaded()
  }

  return (
    <section className={`global-search ${open ? 'open' : ''}`}>
      <div className="global-search-field">
        <span className="global-search-icon" aria-hidden="true">⌕</span>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onFocus={() => { setOpen(true); void ensureLoaded() }}
          onChange={(event) => updateQuery(event.target.value)}
          placeholder="Buscar em toda a Suite: OS, serviço, empreendimento, qualificação, acionamento..."
          aria-label="Busca global da Suite"
          aria-expanded={open && Boolean(query.trim())}
        />
        {query && <button type="button" className="global-search-clear" onClick={() => { updateQuery(''); inputRef.current?.focus() }} aria-label="Limpar busca">×</button>}
        <span className="global-search-shortcut">Ctrl K</span>
      </div>

      {open && query.trim() && (
        <div className="global-search-results" role="listbox" aria-label="Resultados da busca global">
          <div className="global-search-results-head">
            <strong>{loading && !loaded ? 'Carregando bases…' : `${results.length} resultado(s)`}</strong>
            <span>Busca sem acento e sem diferença entre maiúsculas/minúsculas</span>
          </div>
          {!loading && results.length === 0 && <div className="global-search-empty">Nenhum resultado encontrado em toda a Suite.</div>}
          {results.map((result) => (
            <button type="button" key={result.id} onClick={() => select(result)} role="option">
              <span className="global-search-result-group">{result.group}</span>
              <span className="global-search-result-copy">
                <strong>{result.title}</strong>
                <small>{result.subtitle}</small>
              </span>
              <span className="global-search-open">Abrir →</span>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
